/**
 * Labels (MA-61): wat er aan energielabelregistraties binnenkomt in het
 * zoekgebied, en of die adressen daarna te koop komen.
 *
 * Bovenaan de feed: elke registratie van de laatste dagen in de buurten van
 * het zoekgebied, met de woning uit de vangst als het adres daar al staat.
 * De filterbalk kiest periode, klassen, m² (die filtert de API) en wijken en
 * buurten (die filtert het scherm zelf, uit wat de feed teruggaf). Daaronder drie tabellen: per buurt, per
 * dag, en per klasse het deel dat daarna op Funda kwam. De data komt elke nacht
 * binnen via de labelronde (MA-60); zonder die ronde is dit scherm leeg.
 */
import {
  energielabelKleur,
  formatAantal,
  formatDatum,
  formatEuro,
  formatOppervlakte,
  LABELFEED_DAGEN_KEUZES,
  LABELFEED_DAGEN_MAX,
  LABELFEED_DAGEN_STANDAARD,
  LABELFEED_M2_MAX,
  LABELGROEPEN,
  type LabelfeedRij,
  type Labelgroep,
  type LabelOverzicht,
  ZOEKFILTER_LABELS_STANDAARD,
} from '@mamimap/shared';
import { ChevronDown, ExternalLink, Search, X } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { Melding } from '@/components/Melding';
import { useLabelfeed, useLabelOverzicht } from '@/lib/api/queries';
import { Link } from '@/lib/router';
import { KlasseChip } from './LabelKaartje';
import {
  filterOpGebied,
  formatAandeel,
  type Gebiedskeuze,
  gebiedskeuzes,
  woningTekst,
  woningUrl,
  zoeklink,
} from './labelfeed';

/** Een getal uit een invoerveld, of null als het leeg of onzin is. */
function getalOfNull(waarde: string, max: number): number | null {
  if (waarde.trim() === '') return null;
  const getal = Math.round(Number(waarde));
  return Number.isFinite(getal) && getal >= 0 ? Math.min(getal, max) : null;
}

export function LabelsScherm() {
  const [dagen, zetDagen] = useState<number>(LABELFEED_DAGEN_STANDAARD);
  const [groepen, zetGroepen] = useState<Labelgroep[]>([...ZOEKFILTER_LABELS_STANDAARD]);
  const [minM2, zetMinM2] = useState<number | null>(null);
  const [maxM2, zetMaxM2] = useState<number | null>(null);
  const [wijken, zetWijken] = useState<string[]>([]);
  const [buurten, zetBuurten] = useState<string[]>([]);

  /* Geen enkele klasse aan is een lege lijst, geen ongefilterde. */
  const feed = useLabelfeed({ dagen, klassen: 'alle', groepen, minM2, maxM2 }, groepen.length > 0);
  const overzicht = useLabelOverzicht();
  const fout = feed.error ?? overzicht.error;

  const alleRijen = groepen.length > 0 ? (feed.data?.rijen ?? []) : [];
  const keuzes = useMemo(() => gebiedskeuzes(alleRijen), [alleRijen]);
  const rijen = useMemo(
    () => filterOpGebied(alleRijen, { wijken, buurten }),
    [alleRijen, wijken, buurten],
  );
  const gefilterd =
    wijken.length > 0 ||
    buurten.length > 0 ||
    minM2 !== null ||
    maxM2 !== null ||
    dagen !== LABELFEED_DAGEN_STANDAARD ||
    groepen.join() !== ZOEKFILTER_LABELS_STANDAARD.join();
  const wis = () => {
    zetDagen(LABELFEED_DAGEN_STANDAARD);
    zetGroepen([...ZOEKFILTER_LABELS_STANDAARD]);
    zetMinM2(null);
    zetMaxM2(null);
    zetWijken([]);
    zetBuurten([]);
  };
  const eigenDagen = !(LABELFEED_DAGEN_KEUZES as readonly number[]).includes(dagen);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-paper">
      <h1 className="sr-only">Labels</h1>

      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-surface px-4 py-2 text-[12px]">
        <span className="kop text-[15px] text-ink">Nieuwe energielabels</span>
        <fieldset className="flex items-center gap-1">
          <legend className="sr-only">Periode van de feed</legend>
          {LABELFEED_DAGEN_KEUZES.map((keuze) => (
            <button
              key={keuze}
              type="button"
              aria-pressed={dagen === keuze}
              onClick={() => zetDagen(keuze)}
              className={`border px-2 py-1 text-[12px] ${
                dagen === keuze
                  ? 'border-accent text-accent'
                  : 'border-line-strong bg-surface-strong text-ink-soft hover:text-ink'
              }`}
            >
              {keuze} d
            </button>
          ))}
          <input
            id="labels-scherm-dagen"
            type="number"
            min={1}
            max={LABELFEED_DAGEN_MAX}
            inputMode="numeric"
            value={dagen}
            onChange={(event) => {
              const waarde = getalOfNull(event.target.value, LABELFEED_DAGEN_MAX);
              if (waarde !== null && waarde >= 1) zetDagen(waarde);
            }}
            aria-label="Eigen aantal dagen"
            className={`veld w-14 px-1.5 py-1 text-[12px] ${eigenDagen ? 'border-accent' : ''}`}
          />
          <span className="text-ink-soft">dagen</span>
        </fieldset>

        <fieldset className="flex items-center gap-1">
          <legend className="sr-only">Klassen</legend>
          {LABELGROEPEN.map((groep) => {
            const gekozen = groepen.includes(groep);
            return (
              <button
                key={groep}
                type="button"
                aria-pressed={gekozen}
                onClick={() =>
                  zetGroepen(
                    gekozen
                      ? groepen.filter((g) => g !== groep)
                      : LABELGROEPEN.filter((g) => g === groep || groepen.includes(g)),
                  )
                }
                className={`w-7 border py-1 text-center font-medium ${
                  gekozen ? 'border-ink-soft text-ink' : 'border-line text-faint opacity-60'
                }`}
                style={gekozen ? { backgroundColor: energielabelKleur(groep) } : undefined}
              >
                {groep}
              </button>
            );
          })}
        </fieldset>

        <fieldset className="flex items-center gap-1">
          <legend className="sr-only">Oppervlakte in m²</legend>
          <input
            id="labels-scherm-min-m2"
            type="number"
            min={0}
            max={LABELFEED_M2_MAX}
            inputMode="numeric"
            placeholder="min"
            value={minM2 ?? ''}
            onChange={(event) => zetMinM2(getalOfNull(event.target.value, LABELFEED_M2_MAX))}
            aria-label="Minimaal aantal m²"
            className="veld w-16 px-1.5 py-1 text-[12px]"
          />
          <span aria-hidden="true">–</span>
          <input
            id="labels-scherm-max-m2"
            type="number"
            min={0}
            max={LABELFEED_M2_MAX}
            inputMode="numeric"
            placeholder="max"
            value={maxM2 ?? ''}
            onChange={(event) => zetMaxM2(getalOfNull(event.target.value, LABELFEED_M2_MAX))}
            aria-label="Maximaal aantal m²"
            className="veld w-16 px-1.5 py-1 text-[12px]"
          />
          <span className="text-ink-soft">m²</span>
        </fieldset>

        <Meerkeuze titel="Wijk" keuzes={keuzes.wijken} gekozen={wijken} zet={zetWijken} />
        <Meerkeuze titel="Buurt" keuzes={keuzes.buurten} gekozen={buurten} zet={zetBuurten} />

        {gefilterd ? (
          <button
            type="button"
            onClick={wis}
            className="inline-flex items-center gap-1 text-ink-soft hover:text-ink"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            Wis filters
          </button>
        ) : null}

        {feed.data ? (
          <span className="text-ink-soft">
            {formatAantal(rijen.length)} {rijen.length === 1 ? 'label' : 'labels'}
            {feed.data.buurten > 0
              ? ` · ${formatAantal(feed.data.buurten)} buurten van het zoekgebied`
              : ' · geen zoekgebied gekozen, dus alle buurten'}
            {feed.data.laatsteDag
              ? ` · bijgewerkt tot en met ${formatDatum(feed.data.laatsteDag)}`
              : ' · nog geen dagbestand geladen'}
          </span>
        ) : null}
      </header>

      {fout ? (
        <div className="px-4 pt-3">
          <Melding fout={fout} />
        </div>
      ) : null}

      <div className="flex flex-col gap-6 px-4 py-3">
        <Sectie
          titel="In de feed"
          uitleg="Een label is verplicht bij verkoop; een vers label gaat vaak twee tot drie weken aan een Funda-plaatsing vooraf. Een adres dat al in de vangst staat opent de woning."
        >
          {feed.data || groepen.length === 0 ? (
            <Feedtabel rijen={rijen} />
          ) : (
            <p className="text-[12px] text-ink-soft">Laden…</p>
          )}
        </Sectie>

        {overzicht.data ? <Overzicht data={overzicht.data} /> : null}
      </div>
    </div>
  );
}

/**
 * Een uitklaplijst met vinkjes, voor wijken en buurten: alleen de plekken die
 * in de feed voorkomen, met hoeveel labels erin vallen.
 */
function Meerkeuze({
  titel,
  keuzes,
  gekozen,
  zet,
}: {
  titel: string;
  keuzes: readonly Gebiedskeuze[];
  gekozen: readonly string[];
  zet: (codes: string[]) => void;
}) {
  const namen = keuzes.filter((keuze) => gekozen.includes(keuze.code)).map((keuze) => keuze.naam);
  return (
    <details className="relative">
      <summary
        className={`flex cursor-pointer list-none items-center gap-1 border px-2 py-1 ${
          gekozen.length > 0
            ? 'border-accent text-accent'
            : 'border-line-strong bg-surface-strong text-ink-soft hover:text-ink'
        }`}
      >
        <span className="max-w-[22ch] truncate">
          {gekozen.length === 0
            ? `Alle ${titel === 'Wijk' ? 'wijken' : 'buurten'}`
            : gekozen.length === 1
              ? namen[0]
              : `${titel === 'Wijk' ? 'Wijken' : 'Buurten'}: ${gekozen.length}`}
        </span>
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      </summary>
      <div className="absolute left-0 top-full z-20 mt-1 max-h-[60vh] w-64 overflow-y-auto border border-line-strong bg-surface-strong p-1 shadow-md">
        {keuzes.length === 0 ? (
          <p className="px-2 py-1 text-faint">
            Geen {titel === 'Wijk' ? 'wijken' : 'buurten'} in deze lijst.
          </p>
        ) : (
          keuzes.map((keuze) => {
            const aan = gekozen.includes(keuze.code);
            return (
              <label
                key={keuze.code}
                className="flex cursor-pointer items-center gap-2 px-2 py-1 hover:bg-surface"
              >
                <input
                  type="checkbox"
                  checked={aan}
                  onChange={() =>
                    zet(
                      aan
                        ? gekozen.filter((code) => code !== keuze.code)
                        : [...gekozen, keuze.code],
                    )
                  }
                />
                <span className="min-w-0 flex-1 truncate text-ink">{keuze.naam}</span>
                <span className="cijfers text-faint">{formatAantal(keuze.aantal)}</span>
              </label>
            );
          })
        )}
      </div>
    </details>
  );
}

function Sectie({
  titel,
  uitleg,
  children,
}: {
  titel: string;
  uitleg?: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-[13px] font-medium text-ink">{titel}</h2>
      {uitleg ? <p className="max-w-[80ch] text-[12px] text-ink-soft">{uitleg}</p> : null}
      {children}
    </section>
  );
}

function Feedtabel({ rijen }: { rijen: readonly LabelfeedRij[] }) {
  if (rijen.length === 0) {
    return <p className="text-[12px] text-ink-soft">Geen registraties in deze periode.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="tabel text-[12px]">
        <thead>
          <tr>
            <th>Dag</th>
            <th>Adres</th>
            <th>Buurt</th>
            <th>Label</th>
            <th>Opname</th>
            <th className="text-right">m²</th>
            <th className="text-right">Bouwjaar</th>
            <th>Adviseur</th>
            <th>Woning</th>
          </tr>
        </thead>
        <tbody>
          {rijen.map((rij) => (
            <tr key={rij.id}>
              <td className="cijfers whitespace-nowrap">{formatDatum(rij.dag)}</td>
              <td className="whitespace-nowrap font-medium text-ink">
                {rij.woning ? (
                  <Link naar={woningUrl(rij.woning.id)} className="hover:text-accent">
                    {rij.adres}
                  </Link>
                ) : (
                  rij.adres
                )}
              </td>
              <td className="whitespace-nowrap">{rij.buurtnaam ?? '–'}</td>
              <td>
                <KlasseChip rij={rij} />
              </td>
              <td className="cijfers whitespace-nowrap">
                {rij.opnamedatum ? formatDatum(rij.opnamedatum) : '–'}
              </td>
              <td className="cijfers text-right">
                {rij.oppervlakteM2 === null
                  ? '–'
                  : formatOppervlakte(Math.round(rij.oppervlakteM2))}
              </td>
              <td className="cijfers text-right">{rij.bouwjaar ?? '–'}</td>
              <td className="max-w-[22ch] truncate" title={rij.certificaathouder ?? undefined}>
                {rij.certificaathouder ?? '–'}
              </td>
              <td className="whitespace-nowrap">
                <span className={rij.woning ? 'text-accent' : 'text-faint'}>
                  {woningTekst(rij, formatDatum)}
                </span>
                {rij.woning?.vraagprijs ? (
                  <span className="cijfers text-ink-soft">
                    {' '}
                    · {formatEuro(rij.woning.vraagprijs)}
                  </span>
                ) : null}{' '}
                <a
                  href={zoeklink(rij.adres)}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex align-middle text-faint hover:text-accent"
                  aria-label={`${rij.adres} zoeken op het web`}
                  title="Zoeken op het web"
                >
                  {rij.woning ? (
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <Search className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Overzicht({ data }: { data: LabelOverzicht }) {
  const perDag = [...data.perDag].reverse();
  return (
    <>
      <Sectie
        titel={`Per buurt, laatste ${data.dagen} dagen`}
        uitleg="Registraties van bestaande woningen; daarvan in de klassen van het zoekfilter, en daarvan met een woning die daarna in de vangst verscheen."
      >
        <table className="tabel text-[12px]">
          <thead>
            <tr>
              <th>Buurt</th>
              <th className="text-right">Registraties</th>
              <th className="text-right">In filter</th>
              <th className="text-right">Daarna te koop</th>
            </tr>
          </thead>
          <tbody>
            {data.perBuurt.map((rij) => (
              <tr key={rij.buurtcode}>
                <td>{rij.buurtnaam}</td>
                <td className="cijfers text-right">{formatAantal(rij.registraties)}</td>
                <td className="cijfers text-right">{formatAantal(rij.inFilter)}</td>
                <td className="cijfers text-right">{formatAantal(rij.teKoop)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Sectie>

      <Sectie
        titel={`Per dag, laatste ${data.dagen} dagen`}
        uitleg="Toevoegingen in het zoekgebied per dagbestand van RVO, en de verwijderingen: een nieuw label op een adres dat er al een had komt binnen als verwijdering plus toevoeging."
      >
        <table className="tabel text-[12px]">
          <thead>
            <tr>
              <th>Dag</th>
              <th className="text-right">Registraties</th>
              <th className="text-right">In filter</th>
              <th className="text-right">Verwijderingen</th>
            </tr>
          </thead>
          <tbody>
            {perDag.map((rij) => (
              <tr key={rij.dag}>
                <td className="cijfers">{formatDatum(rij.dag)}</td>
                <td className="cijfers text-right">{formatAantal(rij.registraties)}</td>
                <td className="cijfers text-right">{formatAantal(rij.inFilter)}</td>
                <td className="cijfers text-right">{formatAantal(rij.verwijderingen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Sectie>

      <Sectie
        titel="Kwam het te koop?"
        uitleg={`Registraties van ${formatDatum(data.conversie.vanaf)} tot en met ${formatDatum(
          data.conversie.totEnMet,
        )} in het zoekgebied, en het deel met een woning in onze vangst die daarna te koop kwam. De vangst loopt sinds 17 september 2026 en kent alleen de klassen en buurten van het zoekgebied, dus dit is een ondergrens.`}
      >
        <table className="tabel text-[12px]">
          <thead>
            <tr>
              <th>Klasse</th>
              <th className="text-right">Registraties</th>
              <th className="text-right">Daarna te koop</th>
              <th className="text-right">Aandeel</th>
            </tr>
          </thead>
          <tbody>
            {data.conversie.rijen.map((rij) => (
              <tr
                key={rij.klasse ?? 'alle'}
                className={rij.klasse === null ? 'font-medium' : undefined}
              >
                <td>{rij.klasse ?? 'Alle klassen'}</td>
                <td className="cijfers text-right">{formatAantal(rij.registraties)}</td>
                <td className="cijfers text-right">{formatAantal(rij.teKoop)}</td>
                <td className="cijfers text-right">{formatAandeel(rij.aandeel)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Sectie>
    </>
  );
}
